import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  Button,
  Toolbar,
  MenuItem,
  InputAdornment,
  Chip,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import GetAppIcon from '@mui/icons-material/GetApp';
import { activityLogsAPI } from '../../services/api';

export default function ActivityLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    module: '',
    action: '',
    user_id: '',
    start_date: '',
    end_date: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchLogs();
  }, [filters, page]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await activityLogsAPI.getAll({
        page,
        limit: 50,
        ...filters,
      });
      setLogs(response.data);
    } catch (error) {
      console.error('Error loading activity logs', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
    setPage(1);
  };

  const handleExport = () => {
    const csv = [
      ['ID', 'Module', 'Action', 'User', 'Description', 'Timestamp'],
      ...logs.map((log) => [
        log.id,
        log.module,
        log.action,
        log.user_id,
        log.description,
        new Date(log.created_at).toLocaleString('en-IN'),
      ]),
    ]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n');

    const link = document.createElement('a');
    link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    link.download = `activity_logs_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleClearFilters = () => {
    setFilters({
      module: '',
      action: '',
      user_id: '',
      start_date: '',
      end_date: '',
    });
    setSearchTerm('');
    setPage(1);
  };

  const getActionColor = (action) => {
    const colors = {
      CREATE: 'success',
      READ: 'info',
      UPDATE: 'warning',
      DELETE: 'error',
      LOGIN: 'primary',
      LOGOUT: 'secondary',
    };
    return colors[action] || 'default';
  };

  const filteredLogs = logs.filter(
    (log) =>
      log.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: logs.length,
    creates: logs.filter((l) => l.action === 'CREATE').length,
    updates: logs.filter((l) => l.action === 'UPDATE').length,
    deletes: logs.filter((l) => l.action === 'DELETE').length,
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Grid container spacing={2} sx={{ marginBottom: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Box sx={{ fontSize: 24, fontWeight: 'bold', color: '#1976d2' }}>
                {stats.total}
              </Box>
              <Box sx={{ color: '#666', fontSize: 14 }}>Total Logs</Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Box sx={{ fontSize: 24, fontWeight: 'bold', color: '#388e3c' }}>
                {stats.creates}
              </Box>
              <Box sx={{ color: '#666', fontSize: 14 }}>Create Actions</Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Box sx={{ fontSize: 24, fontWeight: 'bold', color: '#f57c00' }}>
                {stats.updates}
              </Box>
              <Box sx={{ color: '#666', fontSize: 14 }}>Update Actions</Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Box sx={{ fontSize: 24, fontWeight: 'bold', color: '#d32f2f' }}>
                {stats.deletes}
              </Box>
              <Box sx={{ color: '#666', fontSize: 14 }}>Delete Actions</Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ marginBottom: 2, padding: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2, gap: 1 }}>
          <FilterListIcon />
          <span style={{ fontWeight: 600 }}>Filters</span>
          {(filters.module || filters.action || filters.user_id || filters.start_date) && (
            <Button
              size="small"
              onClick={handleClearFilters}
              sx={{ marginLeft: 'auto' }}
            >
              Clear All
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr 1fr' }, gap: 2 }}>
          <TextField
            select
            size="small"
            label="Module"
            name="module"
            value={filters.module}
            onChange={handleFilterChange}
          >
            <MenuItem value="">All Modules</MenuItem>
            <MenuItem value="students">Students</MenuItem>
            <MenuItem value="teachers">Teachers</MenuItem>
            <MenuItem value="exams">Exams</MenuItem>
            <MenuItem value="marks">Marks</MenuItem>
            <MenuItem value="attendance">Attendance</MenuItem>
            <MenuItem value="fees">Fees</MenuItem>
            <MenuItem value="users">Users</MenuItem>
            <MenuItem value="classes">Classes</MenuItem>
            <MenuItem value="academic_years">Academic Years</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            label="Action"
            name="action"
            value={filters.action}
            onChange={handleFilterChange}
          >
            <MenuItem value="">All Actions</MenuItem>
            <MenuItem value="CREATE">Create</MenuItem>
            <MenuItem value="READ">Read</MenuItem>
            <MenuItem value="UPDATE">Update</MenuItem>
            <MenuItem value="DELETE">Delete</MenuItem>
            <MenuItem value="LOGIN">Login</MenuItem>
            <MenuItem value="LOGOUT">Logout</MenuItem>
          </TextField>

          <TextField
            size="small"
            label="User ID"
            name="user_id"
            value={filters.user_id}
            onChange={handleFilterChange}
            placeholder="Filter by user ID"
          />

          <TextField
            size="small"
            label="Start Date"
            name="start_date"
            type="date"
            value={filters.start_date}
            onChange={handleFilterChange}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            size="small"
            label="End Date"
            name="end_date"
            type="date"
            value={filters.end_date}
            onChange={handleFilterChange}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Box>

      <Toolbar sx={{ paddingLeft: 0, paddingRight: 0, marginBottom: 2 }}>
        <TextField
          placeholder="Search by user ID or description..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 350, marginRight: 2 }}
        />

        <Button
          variant="outlined"
          startIcon={<GetAppIcon />}
          onClick={handleExport}
          sx={{ marginLeft: 'auto' }}
        >
          Export CSV
        </Button>
      </Toolbar>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>ID</TableCell>
                <TableCell>Module</TableCell>
                <TableCell align="center">Action</TableCell>
                <TableCell>User ID</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Timestamp</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLogs.length > 0 ? (
                filteredLogs.map((log, idx) => (
                  <TableRow key={log.id || idx} hover>
                    <TableCell sx={{ fontSize: 12, color: '#999' }}>{log.id}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{log.module}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={log.action}
                        size="small"
                        color={getActionColor(log.action)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{log.user_id}</TableCell>
                    <TableCell sx={{ fontSize: 13, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {log.description}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                      {new Date(log.created_at).toLocaleString('en-IN')}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ padding: 3 }}>
                    No activity logs found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
